import {
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client/react/hooks"
import { useTranslation } from "react-i18next"

import { callMutation } from "shared/api/callMutation"
import {
  evictAffectedPrices,
  evictParentPricesAfterPositionUpdate
} from "shared/api/price/evictPriceCache"
import {
  invalidateAffectedPrices,
  invalidateParentPricesAfterPositionUpdate
} from "shared/api/price/modifyPriceCache"
import { MutationSchema, MutationTypename } from "shared/api/types"
import { emitAppEvent } from "shared/lib/appEventEmitter"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { MOVE_PROJECT_CHILD } from "shared/types/AppEvent"
import { FloorTypename, ProductPositionTypename } from "shared/types/Project"
import { UPDATE_PRODUCT_POSITION } from "shared/types/ProjectMutations"
import { ProductPositionAddon } from "shared/types/__generated__/graphql"

import { useProductConfigurationQueryWithCacheLazy } from "entities/dim-configuration/api/useProductConfigurationQueryWithCache"
import { getProductConfigurationQueryVariables } from "entities/dim-configuration/lib/getProductConfigurationQueryVariables"
import { getProductPositionAddonsFromConfiguration } from "entities/dim-configuration/lib/getProductPositionAddonsFromConfiguration"
import { FAVORITES_PROJECT_ID } from "entities/favorites/api/useFavoriteMutations"
import { useFavoritesLazyQuery } from "entities/favorites/graphql/queries.generated"
import {
  UpdateProductPositionDocument,
  UpdateProductPositionMutation,
  UpdateProductPositionMutationVariables
} from "entities/product-positions/graphql/mutations.generated"
import { useProductPositionProductInformationLazyQuery } from "entities/product-positions/graphql/queries.generated"
import { getProductType } from "entities/products/lib/getProductType"
import { didMove } from "entities/project-children/lib/didMove"
import { updateProjectChildrenSorting } from "entities/project-children/lib/updateProjectChildrenSorting"
import { evictProjectHistory } from "entities/projects/graphql/project-history/evictProjectHistory"
import { useProjectChildrenLazyQuery } from "entities/projects/graphql/queries.generated"

import { readProductPositionFragment } from "../lib/readProductPositionFragment"
import { prepareProductPositionUpdate } from "../model/prepareProductPositionUpdate"
import {
  ProductPositionSchema,
  UpdateProductPositionParams,
  UpdateProductPositionVariablesSchema
} from "../types/ProductPosition"

function didPriceRelevantAttributesChange(
  input: UpdateProductPositionParams | undefined | null
) {
  return (
    input?.price !== undefined ||
    input?.variantKey !== undefined ||
    input?.configurationKey !== undefined ||
    input?.isAlternative !== undefined ||
    input?.quantity !== undefined ||
    input?.priceGroup !== undefined
  )
}

export function useUpdateProductPosition() {
  const { t } = useTranslation()
  const { cache: apolloCache } = useApolloClient()
  const [fetchConfiguration] = useProductConfigurationQueryWithCacheLazy()
  const [fetchProductInformation] =
    useProductPositionProductInformationLazyQuery({
      fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
    })
  const [fetchProject] = useProjectChildrenLazyQuery()
  const [fetchFavorites] = useFavoritesLazyQuery()
  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.ProductPosition.UPDATE_PRODUCT_POSITION.Start"),
      success: t("Request.ProductPosition.UPDATE_PRODUCT_POSITION.Success"),
      failure: t("Request.ProductPosition.UPDATE_PRODUCT_POSITION.Failure")
    }
  })
  const startProjectRequest = useProjectRequestMonitor()

  const refetch = useEvent((projectID: string) => {
    if (projectID === FAVORITES_PROJECT_ID) {
      fetchFavorites({ fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY })
    } else {
      fetchProject({
        variables: { id: projectID },
        fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
      })
    }
  })

  const [apolloMutate, rest] = useApolloMutation<
    UpdateProductPositionMutation,
    UpdateProductPositionMutationVariables
  >(UpdateProductPositionDocument)

  const updateProductPosition = useEvent(
    async ({
      projectID,
      parentID,
      displayPosition,
      productPosition
    }: {
      projectID: string
      parentID?: string | null
      displayPosition?: number
      productPosition: UpdateProductPositionParams
    }) => {
      const cachedData = readProductPositionFragment(
        productPosition.id,
        apolloCache
      )

      if (!cachedData) {
        // The position is gone (e.g. deleted elsewhere) while its sidebar was still open
        raiseLoadingToast().raiseErrorToast()
        return {
          data: null,
          errors: [
            {
              message: `Unable to read cached data for ProductPosition: ${productPosition.id}`
            }
          ]
        }
      }

      const productType = getProductType(
        productPosition.variantKey || cachedData?.variantKey
      )

      let addons: ProductPositionAddon[] = cachedData?.addons || []

      // Fetch configuration if configurationKey changed
      if (productPosition.configurationKey) {
        const configuration = await fetchConfiguration({
          variables: getProductConfigurationQueryVariables({
            productType: productType ?? "",
            configurationKey: productPosition.configurationKey
          })
        })

        addons = getProductPositionAddonsFromConfiguration(
          configuration.data?.productConfiguration
        )
      }

      const { input, optimisticResponse, moveData } =
        prepareProductPositionUpdate({
          id: productPosition.id,
          displayPosition,
          productPosition,
          addons,
          cache: apolloCache,
          projectID,
          parentID,
          childType: "ProductPosition"
        })

      if (moveData.nextParentType === FloorTypename) {
        return {
          data: null,
          errors: [
            {
              message:
                "ProductPositions cannot be inserted directly into Floors"
            }
          ]
        }
      }

      if (didMove(moveData)) {
        invalidateParentPricesAfterPositionUpdate({
          context: {
            projectID,
            itemID: productPosition.id,
            itemType: ProductPositionTypename,
            ...moveData
          },
          cache: apolloCache
        })

        emitAppEvent(MOVE_PROJECT_CHILD, {
          id: productPosition.id,
          parentID: moveData.nextParentID,
          parentType: moveData.nextParentType,
          position: moveData.nextPosition,
          type: ProductPositionTypename,
          projectID
        })
      }

      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()
      const completeProjectRequest = startProjectRequest({
        projectID,
        requestType: UPDATE_PRODUCT_POSITION
      })

      if (didPriceRelevantAttributesChange(input)) {
        invalidateAffectedPrices({
          projectID,
          itemID: productPosition.id,
          itemType: ProductPositionTypename,
          includeChild: true,
          cache: apolloCache
        })
      }

      return callMutation(apolloMutate, {
        variables: {
          id: productPosition.id,
          input
        },
        transform: data => data.productPosition,
        schemas: {
          input: UpdateProductPositionVariablesSchema,
          response: MutationSchema.extend({
            productPosition: ProductPositionSchema
          })
        },
        apolloOptions: {
          optimisticResponse: optimisticResponse
            ? {
                __typename: MutationTypename,
                productPosition: {
                  ...optimisticResponse.productPosition,
                  addons
                }
              }
            : undefined,
          update: (cache, result) => {
            const productPositionResult = result.data?.productPosition
            if (productPositionResult && didMove(moveData)) {
              updateProjectChildrenSorting({
                movedChild: productPositionResult,
                prevParentID: moveData.prevParentID,
                prevParentType: moveData.prevParentType,
                nextParentID: moveData.nextParentID,
                nextParentType: moveData.nextParentType,
                cache
              })
            }
          },
          onCompleted: () => {
            fetchProductInformation({ variables: { id: productPosition.id } })

            if (didPriceRelevantAttributesChange(input)) {
              evictAffectedPrices({
                projectID,
                itemID: productPosition.id,
                itemType: ProductPositionTypename,
                cache: apolloCache
              })
            }

            if (didMove(moveData)) {
              evictParentPricesAfterPositionUpdate({
                context: {
                  projectID,
                  itemID: productPosition.id,
                  itemType: ProductPositionTypename,
                  ...moveData
                },
                cache: apolloCache
              })
            }
            evictProjectHistory(projectID, apolloCache)
            refetch(projectID)
            completeProjectRequest()
            raiseSuccessToast()
          },
          onError: () => {
            refetch(projectID)
            completeProjectRequest()
            raiseErrorToast()
          }
        }
      })
    }
  )

  return [updateProductPosition, rest] as [
    typeof updateProductPosition,
    typeof rest
  ]
}
